import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MDText } from 'i18n-react';

import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { changeAccountPurchaseDeliveredStatus } from 'Services/api/account';
import { logger } from 'Services/logger/logger';
import { AccountSlugs } from 'Types/account';

import { ACCOUNT_VEHICLE_LIST_QUERY_KEY } from '../useAccountVehicleList';

import LocalTexts from './useUpdateDeliveryStatus.texts';

const LocalT = new MDText(LocalTexts);

export type VehicleMutationPayload = {
	deliveredStatus: boolean;
	enquiryId: number;
};

export type VehicleMutationResponse = { data: { bidId: number; enquiryId: number }; status: string };

type MutationError = {
	config?: {
		data?: string;
	};
};

export const useUpdateDeliveryStatus = (): UseMutationResult<
	VehicleMutationResponse,
	MutationError,
	VehicleMutationPayload
> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ deliveredStatus, enquiryId }: VehicleMutationPayload) =>
			changeAccountPurchaseDeliveredStatus(enquiryId, deliveredStatus),
		onError: (e, variables) => {
			const message = 'There was an issue with fetching bidding counts in account page';
			const error = e instanceof Error ? e : new Error(message);
			logger.error({ context: { extra: { info: message } }, error, message, scope: 'useUpdateDeliveryStatus' });

			toast({
				content: variables.deliveredStatus ? LocalT.translate('errors.marked') : LocalT.translate('errors.unmarked'),
				variant: 'error',
			});
		},
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: [ACCOUNT_VEHICLE_LIST_QUERY_KEY, { slug: AccountSlugs.COMPLETE }],
			});
		},
	});
};
